import { formatDate } from '@angular/common';
import { InjectionToken } from '@angular/core';
import { IntegrationCounts, IntegrationStatus } from '../../models/integration-status';

/**
 * Labels, colours and wording for the integration status drawer. The server decides every status;
 * this only says how each one is shown.
 */

/** The drawer's date format, for the date pipe: dashes, as on the rest of the page (Heinrich, 28/10/2026). */
export const DATE_TIME = 'yyyy-MM-dd HH:mm';
/** Plain FMS days. Formatted without a time zone, so a yyyy-MM-dd day never shifts a day. */
export const DAY = 'yyyy-MM-dd';
/** Instants are shown in SAST whatever the browser's own time zone. */
export const SAST = '+0200';

export type StatusTone = 'success' | 'warning' | 'error' | 'neutral';

const LABELS: Record<IntegrationStatus, string> = {
  UP: 'Up',
  LATE: 'Late',
  DOWN: 'Down',
  CHECK: 'Check',
  NO_RECORD: 'No record yet',
  NOT_SET_UP: 'Not set up'
};

/** Material icon per status, so a pill never relies on its colour alone. */
const ICONS: Record<IntegrationStatus, string> = {
  UP: 'check',
  LATE: 'schedule',
  DOWN: 'close',
  CHECK: 'priority_high',
  NO_RECORD: 'more_horiz',
  NOT_SET_UP: 'remove'
};

const TONES: Record<IntegrationStatus, StatusTone> = {
  UP: 'success',
  LATE: 'warning',
  DOWN: 'error',
  CHECK: 'warning',
  NO_RECORD: 'neutral',
  NOT_SET_UP: 'neutral'
};

/** Worst first, as the server ranks them: Down, then Check and Late, No record yet, Up, Not set up. */
const RANK: Record<IntegrationStatus, number> = {
  DOWN: 50,
  CHECK: 41,
  LATE: 40,
  NO_RECORD: 30,
  UP: 20,
  NOT_SET_UP: 10
};

/**
 * A border and a tint of the tenant's status colour with the normal dark text. Amber text on white
 * fails contrast, so the colour never carries the text. Written out in full so Tailwind finds them.
 */
const PILL: Record<StatusTone, string> = {
  success: 'border-[var(--notification-success)] bg-[var(--notification-success)]/15',
  warning: 'border-[var(--notification-warning)] bg-[var(--notification-warning)]/25',
  error: 'border-[var(--notification-error)] bg-[var(--notification-error)]/15',
  neutral: 'border-[var(--app-border)] bg-[var(--app-base)]'
};

/**
 * The "?" button's badge: the status colour with the status icon on it. The icon is dark, except on
 * the red, where white holds 3:1 (SG #DB4D30: white 4.1:1, #3F3F3F 2.6:1).
 */
const DOT: Record<StatusTone, string> = {
  success: 'bg-[var(--notification-success)] text-[var(--base-text-alt-dark)]',
  warning: 'bg-[var(--notification-warning)] text-[var(--base-text-alt-dark)]',
  error: 'bg-[var(--notification-error)] text-white',
  neutral: 'bg-[var(--base-variant)] text-[var(--base-text-alt-dark)]'
};

export function statusLabel(status: IntegrationStatus): string {
  return LABELS[status] ?? status;
}

export function statusIcon(status: IntegrationStatus): string {
  return ICONS[status] ?? 'help_outline';
}

export function statusTone(status: IntegrationStatus): StatusTone {
  return TONES[status] ?? 'neutral';
}

export function pillClasses(status: IntegrationStatus): string {
  return PILL[statusTone(status)];
}

/** The Field Details card's "Integrations" chip: the same tint and border as a pill, by tone. */
export function chipClasses(tone: StatusTone): string {
  return PILL[tone];
}

export function dotClasses(tone: StatusTone): string {
  return DOT[tone];
}

/** The worst of several statuses; Not set up only when every one is (or there are none). */
export function worstStatus(statuses: IntegrationStatus[]): IntegrationStatus {
  return statuses.reduce<IntegrationStatus>((worst, s) => (RANK[s] ?? 0) > RANK[worst] ? s : worst, 'NOT_SET_UP');
}

/**
 * What the "?" button's badge shows. Null while the first answer is still loading. The icon is the
 * pill's, so the badge does not rely on colour: SG's green and amber are 1.3:1 apart, and all three
 * read as yellow-olive with red-green colour blindness.
 */
export interface StatusDot {
  tone: StatusTone;
  label: string;
  icon: string;
}

/**
 * A failed call is "Status unavailable" in grey, never "Not set up": the field may well be set up.
 * Otherwise the server's overall status, which is Not set up only when all three cards are.
 */
export function statusDot(overall: IntegrationStatus | null, failed: boolean): StatusDot | null {
  if (failed) {
    return { tone: 'neutral', label: 'Status unavailable', icon: 'sync_problem' };
  }
  if (!overall) {
    return null;
  }
  return { tone: statusTone(overall), label: statusLabel(overall), icon: statusIcon(overall) };
}

export interface NamedStatus {
  name: string;
  status: IntegrationStatus;
}

/** The line at the top of "This field": every card up, or the worst cards by name. */
export function fieldHeadline(cards: NamedStatus[]): string {
  if (cards.length > 0 && cards.every(c => c.status === 'UP')) {
    return `All ${cards.length} integrations are up`;
  }
  if (cards.every(c => c.status === 'NOT_SET_UP')) {
    return 'No integrations are set up for this field';
  }
  const worst = worstStatus(cards.map(c => c.status));
  const named = names(cards.filter(c => c.status === worst));
  if (worst === 'UP') {
    const notSetUp = cards.filter(c => c.status === 'NOT_SET_UP');
    return `${named.text} ${named.plural ? 'are' : 'is'} up, ${names(notSetUp).text} ${notSetUp.length > 1 ? 'are' : 'is'} not set up`;
  }
  return `${named.text} ${verb(worst, named.plural)} for this field`;
}

function verb(status: IntegrationStatus, plural: boolean): string {
  switch (status) {
    case 'DOWN': return plural ? 'are down' : 'is down';
    case 'LATE': return plural ? 'are late' : 'is late';
    case 'CHECK': return plural ? 'need a check' : 'needs a check';
    case 'NO_RECORD': return plural ? 'have no record yet' : 'has no record yet';
    default: return plural ? 'are not set up' : 'is not set up';
  }
}

function names(cards: NamedStatus[]): { text: string; plural: boolean } {
  const list = cards.map(c => c.name);
  const text = list.length <= 1 ? (list[0] ?? '') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return { text, plural: list.length > 1 };
}

export interface CountPill {
  status: IntegrationStatus;
  text: string;
}

/** The "All integrations" count strip, worst first, without the statuses nothing is in. */
export function countPills(counts: IntegrationCounts | null): CountPill[] {
  if (!counts) {
    return [];
  }
  const rows: [IntegrationStatus, number, string][] = [
    ['DOWN', counts.down, 'down'],
    ['LATE', counts.late, 'late'],
    ['CHECK', counts.check, counts.check === 1 ? 'needs a check' : 'need a check'],
    ['NO_RECORD', counts.noRecord, 'no record yet'],
    ['UP', counts.up, 'up'],
    ['NOT_SET_UP', counts.notSetUp, 'not set up']
  ];
  return rows.filter(([, n]) => n > 0).map(([status, n, words]) => ({ status, text: `${n} ${words}` }));
}

/** As the server words it: whole km from 10 up, one decimal with a comma below. */
export function formatKm(km: number | null): string | null {
  if (km === null || km === undefined || isNaN(km)) {
    return null;
  }
  return km >= 10 ? String(Math.round(km)) : km.toFixed(1).replace('.', ',');
}

/** Tenant names for ids, in the order given, skipping unknown ids. */
export function tenantNames(ids: number[] | null | undefined, tenants: { id: number; name: string | null }[]): string[] {
  return (ids ?? [])
    .map(id => tenants.find(t => t.id === id)?.name)
    .filter((name): name is string => !!name);
}

/** The reason line under a card's title: tinted when it explains a problem, plain otherwise. */
export function reasonClasses(status: IntegrationStatus): string {
  switch (statusTone(status)) {
    case 'error': return 'rounded-md bg-[var(--notification-error)]/10 px-3 py-2';
    case 'warning': return 'rounded-md bg-[var(--notification-warning)]/15 px-3 py-2';
    default: return '';
  }
}

/**
 * What a status means, for the "?" beside a pill (Heinrich, 28/10/2026: it replaces the legend at the
 * foot of the drawer). The hours and the distance are the server's rules (IntegrationStatusRules).
 */
export function statusMeaning(status: IntegrationStatus, km = 20, weather = false): string {
  if (weather && (status === 'UP' || status === 'LATE' || status === 'DOWN')) {
    // ET0 is calculated once a day and arrives about a day later, so it has a day more (sync-service, 28/10/2026).
    const et0 = { UP: '50', LATE: '50 to 96', DOWN: '96' }[status];
    return {
      UP: 'Every reading is less than 26 hours old',
      LATE: 'A reading is 26 to 72 hours old',
      DOWN: 'A reading is more than 72 hours old, or missing'
    }[status] + ` (ET0 ${et0} hours: it arrives about a day later).`;
  }
  switch (status) {
    case 'UP': return 'Data was pulled in the last 26 hours.';
    case 'LATE': return 'The last good pull was 26 to 72 hours ago.';
    case 'DOWN': return 'Pulls are failing, or the last good pull was more than 72 hours ago.';
    case 'CHECK': return `The weather station is more than ${formatKm(km) ?? km} km from the field, so its weather may not match the field.`;
    case 'NO_RECORD': return 'Linked, but no pull has been recorded yet.';
    case 'NOT_SET_UP': return 'Not linked for this field.';
    default: return '';
  }
}

/** The clock the drawer reads "today" from; a provider in tests pins it. */
export const DRAWER_NOW = new InjectionToken<() => Date>('drawer now', { providedIn: 'root', factory: () => () => new Date() });

/** The SAST calendar day of an instant, yyyy-MM-dd. */
export function sastDay(instant: Date): string {
  return formatDate(instant, 'yyyy-MM-dd', 'en-ZA', SAST);
}

/**
 * Every "yyyy-MM-dd HH:mm" in a line of drawer text, read as SAST, said as "today 22:46" or
 * "yesterday 08:01" when it is that recent, and left as it is otherwise (Heinrich, 28/10/2026: the
 * pull dates read with difficulty).
 */
export function friendlyTimes(text: string | null | undefined, now: Date): string {
  if (!text) return text ?? '';
  const today = sastDay(now);
  const yesterday = sastDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  return text.replace(/(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/g, (whole, day: string, time: string) =>
    day === today ? `today ${time}` : day === yesterday ? `yesterday ${time}` : whole);
}
